import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";
import type { TaskSummary } from "@/api/types";

interface TaskSummaryCardsProps {
  summary: TaskSummary | null;
  loading?: boolean;
  /** Which KPIs are mirrored by the currently-active quick filters.
   *  Multiple can light up at once because the filter pills are
   *  combinable; "pending" lights up as the catch-all when nothing
   *  else is selected. */
  activeKeys?: ReadonlySet<SummaryKey>;
  /** Click on a KPI activates the matching quick filter. Wired only for
   *  the cards that have a 1:1 mapping (overdue / due today / done this
   *  week → completed). Pending stays plain because "no temporal filter"
   *  is already the default state. */
  onSelect?: (key: SummaryKey) => void;
}

export type SummaryKey =
  | "pending"
  | "overdue"
  | "due_today"
  | "completed";

export function TaskSummaryCards({
  summary,
  loading,
  activeKeys,
  onSelect,
}: TaskSummaryCardsProps) {
  const isActive = (k: SummaryKey) => !!activeKeys?.has(k);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Card
        cardKey="pending"
        label="Pending"
        value={summary?.pending}
        icon={Clock}
        iconColor="text-slate-500"
        iconBg="bg-slate-100"
        loading={loading}
        active={isActive("pending")}
        onSelect={onSelect}
      />
      <Card
        cardKey="overdue"
        label="Overdue"
        value={summary?.overdue}
        icon={AlertCircle}
        iconColor="text-rose-600"
        iconBg="bg-rose-100"
        emphasis={(summary?.overdue ?? 0) > 0}
        pulse={(summary?.overdue ?? 0) > 0}
        loading={loading}
        active={isActive("overdue")}
        onSelect={onSelect}
      />
      <Card
        cardKey="due_today"
        label="Due today"
        value={summary?.due_today}
        icon={Calendar}
        iconColor="text-amber-600"
        iconBg="bg-amber-100"
        emphasis={(summary?.due_today ?? 0) > 0}
        loading={loading}
        active={isActive("due_today")}
        onSelect={onSelect}
      />
      <Card
        cardKey="completed"
        label="Done this week"
        value={summary?.completed_this_week}
        icon={CheckCircle2}
        iconColor="text-emerald-600"
        iconBg="bg-emerald-100"
        loading={loading}
        active={isActive("completed")}
        onSelect={onSelect}
      />
    </div>
  );
}

interface CardProps {
  cardKey: SummaryKey;
  label: string;
  value: number | undefined;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  emphasis?: boolean;
  pulse?: boolean;
  loading?: boolean;
  active?: boolean;
  onSelect?: (key: SummaryKey) => void;
}

function Card({
  cardKey,
  label,
  value,
  icon: Icon,
  iconColor,
  iconBg,
  emphasis,
  pulse,
  loading,
  active,
  onSelect,
}: CardProps) {
  const animated = useAnimatedNumber(value ?? 0, 600);
  const interactive = !!onSelect;
  // The Tailwind `animate-pulse` class is reserved for the loading
  // skeleton; for the rose attention pulse we use a custom @keyframes
  // ring (defined inline below) so it doesn't fight the skeleton state.
  const tone = active
    ? "bg-indigo-50/40 ring-1 ring-indigo-200"
    : emphasis
      ? "bg-rose-50/40 ring-1 ring-rose-200"
      : "bg-white";
  const className = cn(
    "flex items-center gap-3 rounded-plane px-4 py-3 shadow-plane transition-[transform,box-shadow] duration-150 motion-reduce:transition-none",
    tone,
    interactive &&
      "cursor-pointer hover:-translate-y-0.5 hover:shadow-plane-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 motion-reduce:hover:translate-y-0",
    pulse && !active && "kpi-pulse",
  );
  const handleClick = onSelect ? () => onSelect(cardKey) : undefined;

  const body = (
    <>
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
          iconBg,
        )}
      >
        <Icon className={cn("h-4 w-4", iconColor)} />
      </div>
      <div className="min-w-0">
        <div className="truncate text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="text-xl font-semibold tabular-nums tracking-tight text-slate-900">
          {loading ? (
            <span className="inline-block h-6 w-10 animate-pulse rounded bg-slate-200" />
          ) : (
            formatNumber(Math.round(animated))
          )}
        </div>
      </div>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={active}
        className={cn(className, "text-left")}
      >
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}
